import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Grid, Html } from '@react-three/drei';
import Editor from '@monaco-editor/react';
import Matter from 'matter-js';
import {
  Box, Button, Stack, Typography, Divider, Paper
} from '@mui/material';

export default function RobotPlayground() {
  // ─── Shared State ─────────────────────────────────────
  const [parts, setParts]           = useState([]);
  const [selectedPartType, setPart] = useState(null);
  const [codeText, setCodeText]     = useState(
    `// try:\n// forward(50);\n// turnLeft(90);\n// forward(50);\n`
  );
  const [commands, setCommands]     = useState([]);

  // parse code → [{fn,arg},…]
  const parse = txt => {
    return txt.split('\n')
      .map(l => l.match(/(\w+)\s*\(\s*(\d+)\s*\)/))
      .filter(m => m)
      .map(([,fn,arg]) => ({ fn, arg:+arg }));
  };

  return (
    <Box sx={{ p:2 }}>
      <Typography variant="h4" gutterBottom>1) Design Your Robot</Typography>
      <Stack direction={{ xs:'column', md:'row' }} spacing={2} alignItems="flex-start">
        <Stack spacing={1} sx={{ width:140 }}>
          {['Body','Wheel','Arm','Sensor'].map(t => (
            <Button
              key={t}
              variant={selectedPartType===t?'contained':'outlined'}
              onClick={()=>setPart(t)}
            >{t}</Button>
          ))}
          <Button color="error" onClick={()=>setParts([])}>Clear All</Button>
        </Stack>
        <RobotDesigner
          parts={parts}
          selectedPartType={selectedPartType}
          onChange={setParts}
        />
      </Stack>

      <Divider sx={{ my:4 }}/>

      <Typography variant="h4" gutterBottom>2) Program It</Typography>
      <Paper sx={{ height:200, mb:2 }}>
        <Editor
          defaultLanguage="javascript"
          defaultValue={codeText}
          onChange={v=>setCodeText(v||'')}
          options={{ minimap:{enabled:false}, wordWrap:'on', fontSize:14 }}
        />
      </Paper>
      <Button variant="contained" onClick={()=>setCommands(parse(codeText))}>
        Parse & Run
      </Button>

      <Divider sx={{ my:4 }}/>

      <Typography variant="h4" gutterBottom>3) Simulate</Typography>
      <Simulator parts={parts} commands={commands}/>
    </Box>
  );
}

// ─── 3D DESIGNER ─────────────────────────────────────────
function RobotDesigner({ parts, selectedPartType, onChange }) {
  const [hovered, setHover] = useState(null);
  const [active,  setActive] = useState(null);

  // place a part on click
  const handlePlace = e => {
    e.stopPropagation();
    if (!selectedPartType) return;
    const { x, z } = e.point;
    onChange([
      ...parts,
      {
        id: Date.now(),
        type: selectedPartType,
        position: [Math.round(x), 0.5, Math.round(z)],
        rotation: [0,0,0],
        scale: 1,
        color: { Body:'#777', Wheel:'#222', Arm:'#c00', Sensor:'#060' }[selectedPartType]
      }
    ]);
  };

  // update one part
  const update = (id, patch) =>
    onChange(parts.map(p=>p.id===id?{...p,...patch}:p));

  // delete one
  const remove = id =>
    onChange(parts.filter(p=>p.id!==id));

  return (
    <Canvas style={{ height:400, flex:1 }}>
      <ambientLight intensity={0.5} />
      <directionalLight position={[5,5,5]} />
      <Grid args={[20,20]} />

      {/* invisible ground */}
      <mesh rotation-x={-Math.PI/2} onClick={handlePlace}>
        <planeGeometry args={[20,20]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>

      {parts.map(p=>(
        <mesh
          key={p.id}
          position={p.position}
          rotation={p.rotation}
          scale={p.scale}
          onPointerOver={e=>{e.stopPropagation();setHover(p.id)}}
          onPointerOut ={()=>setHover(null)}
          onClick={e=>{e.stopPropagation();setActive(p.id)}}
        >
          <boxGeometry args={[1,1,1]} />
          <meshStandardMaterial color={p.color} />
          {active===p.id && (
            <Html center distanceFactor={10}>
              <Box sx={{
                bgcolor:'white', p:1, borderRadius:1,
                boxShadow:2, minWidth:120
              }}>
                <Typography variant="subtitle2">{p.type}</Typography>
                <label>
                  Rotate Y:
                  <input
                    type="range" min={0} max={360}
                    value={p.rotation[1]*180/Math.PI}
                    onChange={e=>update(p.id,{
                      rotation:[0, +e.target.value*Math.PI/180,0]
                    })}
                  />
                </label>
                <br/>
                <label>
                  Scale:
                  <input
                    type="range" min={0.5} max={2} step={0.1}
                    value={p.scale}
                    onChange={e=>update(p.id,{scale:+e.target.value})}
                  />
                </label>
                <br/>
                <Button
                  size="small"
                  color="error"
                  onClick={()=>remove(p.id)}
                >Delete</Button>
              </Box>
            </Html>
          )}
        </mesh>
      ))}

      <OrbitControls />
    </Canvas>
  );
}

// ─── 2D SIMULATOR ────────────────────────────────────────
function Simulator({ parts, commands }) {
  const ref = useRef();
  useEffect(()=>{
    const engine = Matter.Engine.create();
    const world  = engine.world;
    const render = Matter.Render.create({
      element: ref.current,
      engine,
      options:{ width:600, height:300, wireframes:false, background:'#222' }
    });

    // robot body
    const robot = Matter.Bodies.rectangle(100,250,40,40,{
      render:{ fillStyle:'#ddd' }
    });
    Matter.World.add(world, robot);
    Matter.World.add(world,
      Matter.Bodies.rectangle(300,300,600,20,{ isStatic:true })
    );

    Matter.Engine.run(engine);
    Matter.Render.run(render);

    // run commands
    let i=0;
    const next = ()=>{
      if (i>=commands.length) return;
      const {fn,arg} = commands[i++];
      switch(fn){
        case 'forward': Matter.Body.translate(robot,{x:arg,y:0}); break;
        case 'turnLeft':  Matter.Body.rotate(robot,-arg*Math.PI/180); break;
        case 'turnRight': Matter.Body.rotate(robot, arg*Math.PI/180); break;
        case 'wait': /*nothing*/; break;
      }
      setTimeout(next, fn==='wait'?arg:500);
    };
    setTimeout(next,500);

    return ()=>{
      Matter.Render.stop(render);
      Matter.Engine.clear(engine);
      render.canvas.remove();
      render.textures = {};
    };
  },[commands]);

  return <Box ref={ref} sx={{ border:'1px solid #ccc', width:600, height:300 }} />;
}
